import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import {
	MatLegacyDialog as MatDialog,
	MatLegacyDialogConfig as MatDialogConfig
} from "@angular/material/legacy-dialog";
import saveAs from "@shared/utils/fileSaver.js";
import { LegacyPageEvent as PageEvent } from "@angular/material/legacy-paginator";

import { UserCreationDialogComponent } from "./_components/user-creation-dialog/user-creation-dialog.component";
import {
	UserPagination,
	ISearchUserForm,
	ISelectForm,
	User,
	IStatusForm,
	StatusOptions,
	ReportType
} from "./_models/user-management-api.interface";
import { UserService } from "./_services/user.service";
import { SelectOption } from "src/app/shared/models/select-option.interface";
import { ToastService } from "@shared/services/toast/toast.service";
import { InputSelectService } from "../../shared/services/input-select.service";
import { breadCrumbButtonIcon } from "@shared/models/breadcrumbs.interface";
import { UserNameService } from "@shared/services/user-name/user-name.service";

@Component({
	selector: "app-user-management",
	templateUrl: "./user-management.component.html",
	styleUrls: ["./user-management.component.scss"]
})
export class UserManagementComponent implements OnInit, OnDestroy {
	userPagination$!: Observable<UserPagination>;
	searchForm!: FormGroup;
	selectForm!: FormGroup;
	statusForm!: FormGroup;

	destroy$ = new Subject<void>();
	statusOption: SelectOption[] = StatusOptions;
	queryString = "";
	showLog = false;
	isReportsMenuOpen = false;

	userLog!: User;

	/**Pagination*/
	pageSize = 10;
	pageNumber = 0;

	breadCrumbIcon: breadCrumbButtonIcon = {
		name: "add",
		lineWidth: "2.5",
		color: "var(--color-primary-1000)",
		size: "16"
	};

	private fb = inject(FormBuilder);
	private dialog = inject(MatDialog);
	private userService = inject(UserService);
	private toastService = inject(ToastService);
	private inputSelectService = inject(InputSelectService);
	private userNameService = inject(UserNameService);

	ngOnInit(): void {
		this.initSearchForm();
		this.initSelectForm();
		this.initStatuForm();
		this.getUsersDataTable();
		this.userService.getRoles();
	}

	initSearchForm(): void {
		this.searchForm = this.fb.group({
			search: []
		}) as ISearchUserForm;

		this.searchForm
			.get("search")
			?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(400), distinctUntilChanged())
			.subscribe(searchValue => {
				this.queryString = searchValue;
				this.filterUser();
			});
	}

	initSelectForm() {
		this.selectForm = this.fb.group({
			select: []
		}) as ISelectForm;

		this.selectForm
			.get("select")
			?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(400), distinctUntilChanged())
			.subscribe(selectValue => {
				if (selectValue) this.filterUser();
				else this.getUsersDataTable();
			});
	}

	initStatuForm() {
		this.statusForm = this.fb.group({
			status: []
		}) as IStatusForm;

		this.statusForm
			.get("status")
			?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(400), distinctUntilChanged())
			.subscribe(statusValue => {
				if (statusValue) this.filterUser();
				else this.getUsersDataTable();
			});
	}

	getUsersDataTable() {
		this.userPagination$ = this.userService.getUsersList(
			this.pageSize.toString(),
			this.pageNumber.toString(),
			this.queryString
		);
	}

	get rolesList() {
		return this.userService.roles;
	}

	filterUser() {
		const roleValue = this.selectForm.controls.select.value ? this.selectForm.controls.select.value : null;
		const status = this.statusForm.controls.status.value ? this.statusForm.controls.status.value : null;
		const name = this.searchForm.controls.search.value ? this.searchForm.controls.search.value : null;
		this.userPagination$ = this.userService.filterUsers(
			this.pageSize.toString(),
			this.pageNumber.toString(),
			roleValue,
			status,
			name
		);
	}

	addNewUser() {
		this.openUserDialgog().subscribe((data: User | null) => {
			if (data) {
				this.userService.createNewUser(data).subscribe({
					next: () => {
						this.toastService.showSuccess("User Stored Successfully");
						this.getUsersDataTable();
						this.clearAllFilters();
						this.inputSelectService.resetSelectOption();
					},
					error: () => {
						this.toastService.showError("Error Storing User");
					}
				});
			}
		});
	}

	editUser(user: User) {
		this.openUserDialgog(user).subscribe((data: User | null) => {
			if (data) {
				this.userService.updateUser(data).subscribe({
					next: () => {
						this.toastService.showSuccess("User Updated Successfully");
						this.getUsersDataTable();
						this.clearAllFilters();
						this.inputSelectService.resetSelectOption();
						if (data.id) {
							this.userNameService.clearUserCache(data.id);
						}
					},
					error: () => {
						this.toastService.showError("Error Updating User");
					}
				});
			}
		});
	}

	showUserLogInfo(user: User) {
		this.userLog = user;
		this.showLog = true;
	}

	closeLog() {
		this.showLog = false;
	}

	clearAllFilters(): void {
		this.searchForm.get("search")?.patchValue("", { emitEvent: false });
		this.selectForm.get("select")?.patchValue("", { emitEvent: false });
		this.statusForm.get("status")?.patchValue("", { emitEvent: false });
	}

	openUserDialgog(user: User | null = null) {
		const dialogConfig = new MatDialogConfig();

		dialogConfig.disableClose = true;
		dialogConfig.autoFocus = true;
		dialogConfig.panelClass = "delete-dialog-container";
		dialogConfig.width = "1033px";
		if (user) dialogConfig.data = user;

		const dialogRef = this.dialog.open(UserCreationDialogComponent, dialogConfig);
		return dialogRef.afterClosed();
	}

	toggleUser(user: User) {
		this.userService.changeUserStatus(user).subscribe({
			next: () => {
				this.toastService.showSuccess(
					user.blocked ? "User inactivated Successfully" : "User activated Successfully"
				);
				this.getUsersDataTable();
				this.clearAllFilters();
				this.inputSelectService.resetSelectOption();
				this.getUsersDataTable();
			},
			error: () => {
				this.toastService.showError("Error inactivating User");
			}
		});
	}

	paginate(pagination: PageEvent) {
		this.pageSize = pagination.pageSize;
		this.pageNumber = pagination.pageIndex;
		this.filterUser();
	}

	downloadReport(reportType = "users"): void {
		if (reportType === ReportType.Users) {
			this.userService.getUsersReport().subscribe(buffer => {
				const data: Blob = new Blob([buffer], {
					type: "text/csv;charset=utf-8"
				});
				saveAs(data, "users-report.csv");
			});
		} else {
			this.userService.getClientUserReport().subscribe(response => {
				const blob = response.body!;

				let filename = "download"; // fallback

				const contentDisposition = response.headers.get("Content-Disposition");
				if (contentDisposition) {
					const match = contentDisposition.match(/filename="?(.+)"?/);
					if (match && match[1]) {
						filename = match[1];
					}
				}

				saveAs(blob, filename);
			});
		}
	}

	ngOnDestroy(): void {
		//Called once, before the instance is destroyed.
		//Add 'implements OnDestroy' to the class.
		this.destroy$.complete();
	}
}
